import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Infinity as InfinityIcon } from "lucide-react";
import { PRINCIPLE_NAMES, DOCM_MARK } from "@/data/docm";

// Accent colour tokens for each segment.
const ACCENTS = {
  moss:       { fill: "#2D4A3E", text: "#FAF6EE", soft: "#A6B5A5" },
  terracotta: { fill: "#9B4A3A", text: "#FAF6EE", soft: "#E3B3A5" },
  gold:       { fill: "#C9A961", text: "#1C201E", soft: "#F0D9A1" },
};

// Generate the SVG path for one circular segment from `startDeg` to `endDeg`
// on a circle of radius `r` centered at (cx, cy), with an inner cut at `innerR`.
function segmentPath(cx, cy, r, innerR, startDeg, endDeg) {
  const toRad = (d) => (d - 90) * (Math.PI / 180); // rotate so 0deg points up
  const sa = toRad(startDeg);
  const ea = toRad(endDeg);
  const x1 = cx + r * Math.cos(sa);
  const y1 = cy + r * Math.sin(sa);
  const x2 = cx + r * Math.cos(ea);
  const y2 = cy + r * Math.sin(ea);
  const xi1 = cx + innerR * Math.cos(sa);
  const yi1 = cy + innerR * Math.sin(sa);
  const xi2 = cx + innerR * Math.cos(ea);
  const yi2 = cy + innerR * Math.sin(ea);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${xi1.toFixed(2)} ${yi1.toFixed(2)}`,
    `L ${x1.toFixed(2)} ${y1.toFixed(2)}`,
    `A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`,
    `L ${xi2.toFixed(2)} ${yi2.toFixed(2)}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${xi1.toFixed(2)} ${yi1.toFixed(2)}`,
    `Z`,
  ].join(" ");
}

// Polar -> cartesian, used to place segment labels.
function polar(cx, cy, r, deg) {
  const rad = (deg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

export default function PrinciplesWheel() {
  const [hovered, setHovered] = useState(null);
  const total = PRINCIPLE_NAMES.length; // 9
  const sliceDeg = 360 / total;

  // Geometry
  const size = 520;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = 240;
  const innerR = 108;

  const activeIdx = hovered ?? -1;
  const active = activeIdx >= 0 ? PRINCIPLE_NAMES[activeIdx] : null;

  return (
    <section
      id="nine-principles"
      data-testid="principles-wheel-section"
      className="relative py-24 md:py-32 bg-docm-cream overflow-hidden grain"
    >
      {/* Soft halo backdrops */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-docm-gold/8 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-docm-terracotta/8 blur-3xl" />

      <div className="relative max-w-[1300px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left — copy */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 order-2 lg:order-1"
          >
            <span className="label-eyebrow text-docm-terracotta">The 9 Universal Principles</span>
            <h2 className="mt-5 h-display text-4xl md:text-5xl lg:text-[56px] text-docm-ink leading-[1.05]">
              The foundation of a{" "}
              <span className="font-display italic text-docm-moss">conscious civilisation.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-docm-inkSoft leading-relaxed">
              Nine principles, one shared compass. The Declaration of Consciousness
              responds to humanity&rsquo;s fundamental need for peace, unity, and
              belonging in a changing world. Together, these principles celebrate
              all dimensions of consciousness and translate awareness into everyday
              action — from the worth of one person to the responsibility of an
              entire planet.
            </p>

            {/* Live read-out — shows current hovered/selected principle */}
            <div
              data-testid="wheel-readout"
              className="mt-8 rounded-2xl border border-docm-border bg-docm-paper/70 backdrop-blur-sm p-5 min-h-[148px]"
            >
              {active ? (
                <>
                  <div className="flex items-baseline gap-3">
                    <span className="font-serif-h text-2xl text-docm-terracotta tabular-nums">
                      {active.num}
                    </span>
                    <h3 className="font-serif-h text-xl md:text-2xl text-docm-ink leading-tight">
                      {active.label}
                    </h3>
                  </div>
                  <p className="mt-1 font-display italic text-[15px] md:text-base text-docm-moss leading-snug">
                    {active.tagline}
                  </p>
                  <p
                    data-testid="wheel-readout-description"
                    className="mt-2.5 text-[14.5px] md:text-[15.5px] text-docm-inkSoft leading-relaxed"
                  >
                    {active.description}
                  </p>
                </>
              ) : (
                <p className="text-sm text-docm-inkSoft italic leading-relaxed">
                  Hover or tap a segment to read each principle. Click through for the full essay.
                </p>
              )}
            </div>

            <Link
              to="/about#principles"
              data-testid="wheel-explore-cta"
              className="btn-primary mt-7 inline-flex group"
            >
              Explore all 9 principles
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>

          {/* Right — the wheel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-7 order-1 lg:order-2 flex justify-center"
          >
            <div className="relative w-full max-w-[520px] aspect-square">
              <svg
                viewBox={`0 0 ${size} ${size}`}
                width="100%"
                height="100%"
                role="img"
                aria-labelledby="principles-wheel-title"
                data-testid="principles-wheel-svg"
              >
                <title id="principles-wheel-title">The 9 Universal Principles of DOCM</title>

                {/* Outer faint ring */}
                <circle cx={cx} cy={cy} r={outerR + 12} fill="none" stroke="#E8E1D2" strokeWidth="0.7" />
                <circle cx={cx} cy={cy} r={outerR + 28} fill="none" stroke="#E8E1D2" strokeWidth="0.4" strokeDasharray="2 6" />

                {PRINCIPLE_NAMES.map((p, i) => {
                  const start = i * sliceDeg;
                  const end = start + sliceDeg;
                  const mid = start + sliceDeg / 2;
                  const colour = ACCENTS[p.accent] || ACCENTS.moss;
                  const isActive = activeIdx === i;
                  const labelR = (outerR + innerR) / 2;
                  const label = polar(cx, cy, labelR, mid);
                  // Number sits directly above its label, on the same vertical
                  // axis, so every segment reads "number over word" consistently.
                  const numPos = { x: label.x, y: label.y - 25 };
                  return (
                    <g
                      key={p.label}
                      data-testid={`wheel-segment-${i + 1}`}
                      onMouseEnter={() => setHovered(i)}
                      onMouseLeave={() => setHovered(null)}
                      onFocus={() => setHovered(i)}
                      onBlur={() => setHovered(null)}
                      tabIndex={0}
                      style={{ cursor: "pointer", outline: "none" }}
                    >
                      <path
                        d={segmentPath(cx, cy, outerR, innerR, start + 0.6, end - 0.6)}
                        fill={isActive ? colour.fill : "#FFFDF8"}
                        stroke={colour.fill}
                        strokeWidth={isActive ? 1.4 : 0.9}
                        style={{
                          transition: "fill 280ms ease, stroke-width 280ms ease, transform 280ms ease",
                          transformOrigin: `${cx}px ${cy}px`,
                          transform: isActive ? "scale(1.035)" : "scale(1)",
                          filter: isActive ? "drop-shadow(0 12px 22px rgba(28,32,30,0.18))" : "none",
                        }}
                      />
                      {/* Segment number — small, near outer rim */}
                      <text
                        x={numPos.x}
                        y={numPos.y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontFamily="Cormorant Garamond, Spectral, serif"
                        fontSize="15"
                        fontWeight="600"
                        fill={isActive ? colour.soft : colour.fill}
                        style={{ transition: "fill 280ms ease" }}
                      >
                        {p.num}
                      </text>
                      {/* Segment label — short word, centred mid-segment */}
                      <text
                        x={label.x}
                        y={label.y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontFamily="Cormorant Garamond, Spectral, serif"
                        fontSize="18.5"
                        fontWeight="700"
                        fill={isActive ? colour.text : "#1C201E"}
                        style={{ transition: "fill 280ms ease", pointerEvents: "none" }}
                      >
                        {p.label}
                      </text>
                    </g>
                  );
                })}

                {/* Inner medallion */}
                <circle cx={cx} cy={cy} r={innerR - 6} fill="#FAF6EE" stroke="#C9A961" strokeWidth="1" />
                <circle cx={cx} cy={cy} r={innerR + 6} fill="none" stroke="#C9A961" strokeOpacity="0.35" strokeWidth="0.6" strokeDasharray="2 5" />
              </svg>

              {/* Center logo + tagline overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex flex-col items-center text-center px-2 max-w-[170px]">
                  <img
                    src={DOCM_MARK}
                    alt="DOCM"
                    className="w-[88px] h-[88px] md:w-[100px] md:h-[100px] object-contain mb-2"
                    data-testid="wheel-center-logo"
                  />
                  <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] text-docm-gold font-semibold">
                    <InfinityIcon size={10} />
                    <span>9 Principles</span>
                  </div>
                  <div className="mt-1 font-display italic text-[10.5px] text-docm-inkSoft leading-snug">
                    One compass.
                    <br />
                    One conscious civilisation.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
